import { ChevronDown } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type KeyboardEvent } from 'react';
import type {
  BlueprintDocument,
  ParameterDefinition,
  ResourceType,
  SecretChangePolicy,
  SecretDeclaration,
} from '../api/types';
import { FieldMessages } from '../components/ui';
import { CompletionSourceContext, type CompletionSource } from '../forms/completion';
import { ExpressionInput } from '../forms/ExpressionInput';
import { secretReference } from '../lib/expressions';
import { GENERATE_MAX_LENGTH, GENERATE_MIN_LENGTH, secretNameProblem } from '../lib/secrets';
import { MenuButton, type MenuEntry } from '../shell/Menu';
import { strings } from '../strings';
import { classifyValue, secretNameFor, SETTING_NAME } from './model';

const s = strings.variables.edit;
const PARAMETER_NAME = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;
const NEW = '\0new';

export type DesignSource = 'plain' | 'parameter' | 'output' | 'secret';

/** What an add or change-source edit commits: one variable, and a declaration added in the same step. */
export interface VariableEdit {
  resourceId: string;
  name: string;
  value: string;
  declareSecret?: { name: string; declaration: SecretDeclaration };
  declareParameter?: { name: string; definition: ParameterDefinition };
}

/**
 * The inline editor of design-scope variables (spec 6.10.10): Add variable (name, source picker, the source's
 * editor) and Change source (the same, for an existing variable).
 */
export function VariableForm({
  mode,
  doc,
  types,
  apps,
  initial,
  takenNames,
  onSubmit,
  onCancel,
}: {
  mode: 'add' | 'change';
  doc: BlueprintDocument;
  types: ReadonlyMap<string, ResourceType>;
  /** Apps the variable can be added to (add) or its app (change). */
  apps: readonly string[];
  initial?: { resourceId: string; name: string; value: string };
  /** Variable names already used, by app. */
  takenNames: (resourceId: string) => readonly string[];
  onSubmit: (edit: VariableEdit) => void;
  onCancel: () => void;
}) {
  const ids = useId();
  const current = initial ? classifyValue(initial.value) : null;
  const [resourceId, setResourceId] = useState(initial?.resourceId ?? apps[0] ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [source, setSource] = useState<DesignSource>(
    !current || current.type === 'expression' ? 'plain' : current.type,
  );
  const [value, setValue] = useState(
    current?.type === 'plain' || current?.type === 'expression' ? initial!.value : '',
  );
  const parameters = Object.keys(doc.parameters ?? {});
  const [parameter, setParameter] = useState(current?.parameter ?? parameters[0] ?? NEW);
  const [newParameter, setNewParameter] = useState('');
  const others = doc.resources.filter(
    (r) => r.id !== resourceId && (types.get(r.type)?.outputs.length ?? 0) > 0,
  );
  const [outputResource, setOutputResource] = useState(current?.output?.resourceId ?? others[0]?.id ?? '');
  const outputs = types.get(doc.resources.find((r) => r.id === outputResource)?.type ?? '')?.outputs ?? [];
  const [output, setOutput] = useState(current?.output?.output ?? outputs[0]?.name ?? '');
  const declared = Object.keys(doc.secrets ?? {});
  const fromPlain = mode === 'change' && (current?.type === 'plain' || current?.type === 'expression');
  const [secret, setSecret] = useState(current?.secretName ?? (fromPlain ? NEW : (declared[0] ?? NEW)));
  const [newSecret, setNewSecret] = useState(fromPlain && initial ? secretNameFor(initial.name) : '');
  const [description, setDescription] = useState('');
  const [onChange, setOnChange] = useState<SecretChangePolicy>('wait');
  const [generate, setGenerate] = useState(false);
  const [length, setLength] = useState(32);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const completion = useMemo<CompletionSource>(
    () => ({
      parameters,
      resources: doc.resources
        .filter((r) => r.id !== resourceId)
        .map((r) => ({ id: r.id, outputs: types.get(r.type)?.outputs ?? [] })),
      secrets: declared,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [doc, resourceId, types],
  );

  const chosenSecret = declared.includes(secret) ? secret : NEW;
  const chosenParameter = parameters.includes(parameter) ? parameter : NEW;

  const submit = () => {
    const next: Record<string, string> = {};
    if (mode === 'add') {
      if (!SETTING_NAME.test(name)) next.name = s.nameInvalid;
      else if (takenNames(resourceId).includes(name)) next.name = s.nameTaken;
    }
    const edit: VariableEdit = { resourceId, name: mode === 'add' ? name : initial!.name, value: '' };
    if (source === 'plain') edit.value = value;
    else if (source === 'parameter') {
      if (chosenParameter === NEW) {
        if (!PARAMETER_NAME.test(newParameter)) next.parameter = s.parameterInvalid;
        else if (parameters.includes(newParameter)) next.parameter = s.parameterTaken;
        edit.declareParameter = { name: newParameter, definition: { type: 'string' } };
        edit.value = '${param.' + newParameter + '}';
      } else edit.value = '${param.' + chosenParameter + '}';
    } else if (source === 'output') {
      if (!outputResource || !output) next.output = s.noOutputs;
      edit.value = '${resource.' + outputResource + '.' + output + '}';
    } else {
      if (chosenSecret === NEW) {
        const problem = secretNameProblem(newSecret, declared);
        if (problem) next.secret = strings.declarations.nameProblem[problem];
        const declaration: SecretDeclaration = { onChange };
        if (description) declaration.description = description;
        if (generate) declaration.generate = { length };
        edit.declareSecret = { name: newSecret, declaration };
        edit.value = secretReference(newSecret);
      } else edit.value = secretReference(chosenSecret);
    }
    setErrors(next);
    if (Object.keys(next).length === 0) onSubmit(edit);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (event.key === 'Escape' && !event.defaultPrevented) {
      event.preventDefault();
      onCancel();
    } else if (
      event.key === 'Enter' &&
      !event.defaultPrevented &&
      (event.target as HTMLElement).tagName === 'INPUT' &&
      (event.target as HTMLInputElement).type !== 'checkbox'
    ) {
      event.preventDefault();
      submit();
    }
  };

  const sources = s.sources;
  const pick = (next: DesignSource) => () => setSource(next);
  const entries: MenuEntry[] = [
    { kind: 'note', id: 'g1', label: s.groups.blueprint },
    {
      kind: 'radio',
      id: 'plain',
      label: sources.plain,
      checked: source === 'plain',
      onSelect: pick('plain'),
    },
    {
      kind: 'radio',
      id: 'parameter',
      label: (
        <span className="flex w-full justify-between gap-4">
          {sources.parameter} <span className="text-fg-muted">{sources.parameterHint}</span>
        </span>
      ),
      checked: source === 'parameter',
      onSelect: pick('parameter'),
    },
    {
      kind: 'radio',
      id: 'output',
      label: sources.output,
      checked: source === 'output',
      onSelect: pick('output'),
    },
    { kind: 'separator', id: 's1' },
    { kind: 'note', id: 'g2', label: s.groups.deployment },
    {
      kind: 'radio',
      id: 'secret',
      label: (
        <span className="flex w-full justify-between gap-4">
          {sources.secret} <span className="text-fg-muted">{sources.secretHint}</span>
        </span>
      ),
      checked: source === 'secret',
      onSelect: pick('secret'),
    },
    { kind: 'separator', id: 's2' },
    { kind: 'note', id: 'g3', label: s.groups.connections },
    {
      kind: 'action',
      id: 'connection',
      label: sources.connection,
      disabledReason: sources.connectionHint,
      onSelect: () => {},
    },
    {
      kind: 'action',
      id: 'trigger',
      label: sources.trigger,
      disabledReason: sources.triggerHint,
      onSelect: () => {},
    },
  ];

  return (
    <CompletionSourceContext.Provider value={completion}>
      <div
        role="group"
        aria-label={mode === 'add' ? s.addTitle : s.changeSourceTitle(initial?.name ?? '')}
        className="var-form"
        onKeyDown={onKeyDown}
      >
        {mode === 'add' && apps.length > 1 && (
          <div className="var-form-field w-36">
            <label className="label" htmlFor={`${ids}-app`}>
              {s.resource}
            </label>
            <select
              id={`${ids}-app`}
              className="input"
              value={resourceId}
              onChange={(event) => setResourceId(event.target.value)}
            >
              {apps.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
          </div>
        )}
        {mode === 'add' && (
          <div className="var-form-field w-56">
            <label className="label" htmlFor={`${ids}-name`}>
              {s.name}
            </label>
            <input
              id={`${ids}-name`}
              className="input font-mono"
              value={name}
              autoFocus
              aria-invalid={errors.name ? true : undefined}
              aria-describedby={`${ids}-name-help`}
              onChange={(event) => setName(event.target.value)}
            />
            <p id={`${ids}-name-help`} className="sr-only">
              {s.nameHelp}
            </p>
            <FieldMessages messages={errors.name ? [errors.name] : []} />
          </div>
        )}
        <div className="var-form-field">
          <span className="label" id={`${ids}-source-label`}>
            {s.source}
          </span>
          <MenuButton
            portal
            buttonClassName="btn var-source-picker"
            buttonLabel={`${s.source}: ${sources[source]}`}
            buttonContent={
              <>
                {sources[source]} <ChevronDown aria-hidden className="size-3.5" />
              </>
            }
            menuLabel={s.chooseSource}
            entries={entries}
          />
        </div>
        <div className="var-form-field min-w-64 flex-1">
          {source === 'plain' && (
            <>
              <label className="label" htmlFor={`${ids}-value`}>
                {s.value}
              </label>
              <ExpressionInput
                id={`${ids}-value`}
                monospace
                value={value}
                kinds={['param', 'resource', 'deployment']}
                onChange={setValue}
              />
            </>
          )}
          {source === 'parameter' && (
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="label" htmlFor={`${ids}-parameter`}>
                  {s.parameter}
                </label>
                <select
                  id={`${ids}-parameter`}
                  className="input font-mono"
                  value={chosenParameter}
                  onChange={(event) => setParameter(event.target.value)}
                >
                  {parameters.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                  <option value={NEW}>{s.newParameter}</option>
                </select>
              </div>
              {chosenParameter === NEW && (
                <div className="flex-1">
                  <label className="label" htmlFor={`${ids}-new-parameter`}>
                    {s.newParameterName}
                  </label>
                  <input
                    id={`${ids}-new-parameter`}
                    className="input font-mono"
                    value={newParameter}
                    aria-invalid={errors.parameter ? true : undefined}
                    onChange={(event) => setNewParameter(event.target.value)}
                  />
                  <FieldMessages messages={errors.parameter ? [errors.parameter] : []} />
                </div>
              )}
            </div>
          )}
          {source === 'output' && (
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="label" htmlFor={`${ids}-output-resource`}>
                  {s.outputResource}
                </label>
                <select
                  id={`${ids}-output-resource`}
                  className="input font-mono"
                  value={outputResource}
                  onChange={(event) => {
                    setOutputResource(event.target.value);
                    const type = doc.resources.find((r) => r.id === event.target.value)?.type ?? '';
                    setOutput(types.get(type)?.outputs[0]?.name ?? '');
                  }}
                >
                  {others.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.id}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="label" htmlFor={`${ids}-output`}>
                  {s.output}
                </label>
                <select
                  id={`${ids}-output`}
                  className="input font-mono"
                  value={output}
                  onChange={(event) => setOutput(event.target.value)}
                >
                  {outputs.map((o) => (
                    <option key={o.name} value={o.name}>
                      {o.name}
                      {o.secret ? ` (${strings.common.secret})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <FieldMessages messages={errors.output ? [errors.output] : []} />
            </div>
          )}
          {source === 'secret' && (
            <div className="flex flex-wrap gap-2">
              <div className="min-w-40 flex-1">
                <label className="label" htmlFor={`${ids}-secret`}>
                  {s.secret}
                </label>
                <select
                  id={`${ids}-secret`}
                  className="input font-mono"
                  value={chosenSecret}
                  onChange={(event) => setSecret(event.target.value)}
                >
                  {declared.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                  <option value={NEW}>
                    {fromPlain && newSecret ? s.createSecret(newSecret) : s.newSecret}
                  </option>
                </select>
              </div>
              {chosenSecret === NEW && (
                <>
                  <div className="min-w-40 flex-1">
                    <label className="label" htmlFor={`${ids}-new-secret`}>
                      {s.newSecretName}
                    </label>
                    <input
                      id={`${ids}-new-secret`}
                      className="input font-mono"
                      value={newSecret}
                      aria-invalid={errors.secret ? true : undefined}
                      onChange={(event) => setNewSecret(event.target.value)}
                    />
                  </div>
                  <div className="min-w-40 flex-1">
                    <label className="label" htmlFor={`${ids}-description`}>
                      {s.description}
                    </label>
                    <input
                      id={`${ids}-description`}
                      className="input"
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                    />
                  </div>
                  <div className="w-36">
                    <label className="label" htmlFor={`${ids}-on-change`}>
                      {s.onChange}
                    </label>
                    <select
                      id={`${ids}-on-change`}
                      className="input"
                      value={onChange}
                      onChange={(event) => setOnChange(event.target.value as SecretChangePolicy)}
                    >
                      <option value="wait">wait</option>
                      <option value="restart">restart</option>
                    </select>
                  </div>
                  <div className="flex items-end gap-2">
                    <label className="flex items-center gap-1.5 pb-1.5 text-[13px]">
                      <input
                        type="checkbox"
                        checked={generate}
                        onChange={(event) => setGenerate(event.target.checked)}
                      />
                      {s.generate}
                    </label>
                    {generate && (
                      <div className="w-20">
                        <label className="label" htmlFor={`${ids}-length`}>
                          {s.length}
                        </label>
                        <input
                          id={`${ids}-length`}
                          type="number"
                          className="input"
                          min={GENERATE_MIN_LENGTH}
                          max={GENERATE_MAX_LENGTH}
                          value={length}
                          onChange={(event) =>
                            setLength(
                              Math.max(
                                GENERATE_MIN_LENGTH,
                                Math.min(GENERATE_MAX_LENGTH, Number(event.target.value) || 32),
                              ),
                            )
                          }
                        />
                      </div>
                    )}
                  </div>
                  <div className="w-full">
                    <FieldMessages messages={errors.secret ? [errors.secret] : []} />
                    {fromPlain && <p className="help">{s.plainNotCopied}</p>}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        <div className="var-form-actions">
          <button type="button" className="btn" onClick={onCancel}>
            {s.cancel}
          </button>
          <button type="button" className="btn btn-primary" onClick={submit}>
            {mode === 'add' ? s.add : s.apply}
          </button>
        </div>
      </div>
    </CompletionSourceContext.Provider>
  );
}

/** Inline editors of one cell: a variable's name (rename) or a plain or expression value. */
export function InlineInput({
  label,
  initial,
  expression,
  validate,
  onCommit,
  onCancel,
}: {
  label: string;
  initial: string;
  /** Offer `${` completion (values). */
  expression?: boolean;
  validate?: (value: string) => string | null;
  onCommit: (value: string) => void;
  onCancel: () => void;
}) {
  const id = useId();
  const [value, setValue] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const input = document.getElementById(id) as HTMLInputElement | null;
    input?.focus();
    input?.select();
  }, [id]);
  const commit = () => {
    const problem = validate?.(value) ?? null;
    setError(problem);
    if (!problem) onCommit(value);
  };
  return (
    <div
      className="w-full"
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.defaultPrevented) return;
        if (event.key === 'Enter') {
          event.preventDefault();
          commit();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          onCancel();
        }
      }}
    >
      {expression ? (
        <ExpressionInput
          id={id}
          monospace
          value={value}
          kinds={['param', 'resource', 'deployment']}
          aria-label={label}
          aria-invalid={error ? true : undefined}
          onChange={setValue}
          onBlur={commit}
        />
      ) : (
        <input
          id={id}
          className="input font-mono"
          aria-label={label}
          value={value}
          aria-invalid={error ? true : undefined}
          onChange={(event) => setValue(event.target.value)}
          onBlur={commit}
        />
      )}
      {error && <p className="field-error">{error}</p>}
    </div>
  );
}
